import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Camera, RefreshCw, Loader2, Share2, Download, Heart, CalendarDays, ChevronLeft, ChevronUp, ChevronDown, Sparkles, X, Bell, GitCompareArrows, Frame, Film, Layers, SwitchCamera, Video } from "lucide-react";
import api, { getFavorites } from "@/lib/api";
import { toast } from "sonner";
import MirrorReelMode from "@/components/app/MirrorReelMode";
import FaceMaskDrawer from "@/components/app/FaceMaskDrawer";
import { compositeFacePreservation } from "@/lib/faceComposite";
import { swapFaceOnModel } from "@/lib/freeMirrorSwap";

/* Customer-facing background presets — keys must match the backend's
   MIRROR_BACKGROUNDS dict in /app/backend/server.py. */
const BACKGROUND_PRESETS = [
    { key: "boutique",     emoji: "🛍️", label: "Boutique",   desc: "Beige studio with warm light" },
    { key: "palace",       emoji: "🏛️", label: "Palace",     desc: "Indian palace courtyard" },
    { key: "garden",       emoji: "🌿", label: "Garden",     desc: "Mughal garden, soft greenery" },
    { key: "sunset",       emoji: "🌅", label: "Sunset",     desc: "Golden hour outdoor scene" },
    { key: "studio_white", emoji: "⬜", label: "Studio",     desc: "Clean white studio backdrop" },
    { key: "original",     emoji: "📍", label: "My place",   desc: "Keep my real background" },
];

// Cached MediaPipe Pose loader — analyses the customer photo once and returns
// a coarse body-shape category. Returns `null` if the library fails to load
// (e.g. offline) so the rest of the mirror keeps working.
async function analyzeBodyShape(imageDataUrl) {
    try {
        const tasksVision = await import(/* webpackIgnore: true */ "https://cdn.jsdelivr.net/npm/@mediapipe/[email protected]/+esm");
        const { PoseLandmarker, FilesetResolver } = tasksVision;
        const vision = await FilesetResolver.forVisionTasks(
            "https://cdn.jsdelivr.net/npm/@mediapipe/[email protected]/wasm",
        );
        const landmarker = await PoseLandmarker.createFromOptions(vision, {
            baseOptions: { modelAssetPath: "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task" },
            runningMode: "IMAGE",
            numPoses: 1,
        });
        const img = await new Promise((resolve, reject) => {
            const i = new Image();
            i.onload = () => resolve(i);
            i.onerror = reject;
            i.src = imageDataUrl;
        });
        const result = landmarker.detect(img);
        landmarker.close();
        const lm = result.landmarks?.[0];
        if (!lm) return null;
        // BlazePose landmark indices: 11 L-shoulder, 12 R-shoulder, 23 L-hip, 24 R-hip
        const dist = (a, b) => Math.hypot((a.x - b.x), (a.y - b.y));
        const shoulderW = dist(lm[11], lm[12]);
        const hipW = dist(lm[23], lm[24]);
        if (!shoulderW || !hipW) return null;
        const ratio = shoulderW / hipW;
        // Coarse 4-bucket classification — same buckets used by tailors
        if (ratio > 1.12) return { shape: "Inverted Triangle", hint: "Wide shoulders. Try sarees with light pallu, heavy borders, contrast pleats." };
        if (ratio < 0.92) return { shape: "Pear", hint: "Defined hips. Try heavily-worked pallu, lighter pleats, dark borders below the waist." };
        if (Math.abs(ratio - 1) < 0.06) return { shape: "Rectangle", hint: "Even silhouette. Try ruffled / pleated sarees, embroidered borders to create curves." };
        return { shape: "Hourglass", hint: "Balanced shoulders and hips. Most drapes will flatter — try mermaid wraps and sheer sarees." };
    } catch {
        return null;
    }
}

/* Smart Mirror try-on page — runs on any tablet, laptop, or smart-mirror hardware.
   URL: /mirror/{shop-slug} */
export default function Mirror() {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    /* Optional ?saree={id} query param — set when a customer scans a per-saree
       hanger QR tag (printed via the "Print saree QR tags" feature). When
       present, the Mirror skips the "pick a saree" UX and tries this saree
       on right after the photo capture. */
    const preselectedSareeId = searchParams.get("saree");
    /* Optional ?category={cat-slug} — set when customer scans a category QR
       poster (printed via "Print category QR posters" in Shop Admin). Only
       sarees in that category are shown in the picker / Reel mode / Recommended.
       The customer can also change this in-app via the category chip row. */
    const urlCategory = searchParams.get("category");
    const [activeCategory, setActiveCategory] = useState(urlCategory || "");  // "" = all, "__favorites" = favorites only
    const [categories, setCategories] = useState([]);   // [{slug, name, count}]
    const [favoriteIds, setFavoriteIds] = useState(() => new Set(getFavorites().filter((f) => f.shop_slug === slug).map((f) => f.id)));
    const [shop, setShop] = useState(null);
    const [sarees, setSarees] = useState([]);
    const [categoryName, setCategoryName] = useState(null);   // for header display
    const [stage, setStage] = useState("welcome");   // welcome | capture | tryon
    const [customerPhoto, setCustomerPhoto] = useState(null);
    const [activeSareeId, setActiveSareeId] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [tryOnImg, setTryOnImg] = useState(null);
    // Face-preservation mask: {x_pct, y_pct, r_pct} normalized 0..1 — set once
    // after the customer captures/uploads their photo. Persists for the session.
    const [faceMask, setFaceMask] = useState(null);
    const [maskingOpen, setMaskingOpen] = useState(false);
    // Cinematic Ken-Burns reel produced from the try-on image (Tier 1, free).
    // Keyed by activeSareeId so switching sarees clears the previous reel.
    const [reelUrl, setReelUrl] = useState(null);
    const [reelBusy, setReelBusy] = useState(false);
    const [sessionId] = useState(() => `m-${Math.random().toString(36).slice(2)}-${Date.now()}`);
    const [bodyShape, setBodyShape] = useState(null);   // { shape, hint } | null
    const [analyzing, setAnalyzing] = useState(false);
    const [recommendedIds, setRecommendedIds] = useState([]);   // ranked top-N saree ids
    const [background, setBackground] = useState("boutique");   // backdrop preset key
    const [reelMode, setReelMode] = useState(false);    // Instagram-style vertical scroll
    // Countdown auto-capture
    const [countdown, setCountdown] = useState(0);       // 0 = idle, 3/2/1 = counting
    const [flash, setFlash] = useState(false);
    const countdownTimerRef = useRef(null);
    // Compare mode
    const [compareMode, setCompareMode] = useState(false);
    const [compareSareeId, setCompareSareeId] = useState(null);
    const [compareImg, setCompareImg] = useState(null);
    const [compareLoading, setCompareLoading] = useState(false);
    // Staff request
    const [staffSent, setStaffSent] = useState(false);
    const [staffSending, setStaffSending] = useState(false);
    const [staffOpen, setStaffOpen] = useState(false);
    const [staffName, setStaffName] = useState("");
    const [staffPhone, setStaffPhone] = useState("");
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const cache = useRef({});  // sareeId → tryOn image (per-session)

    // Load shop + sarees + category list
    useEffect(() => {
        (async () => {
            try {
                // If a category filter is set, ask the API for that category only —
                // saves bandwidth + ensures parallel pre-warming targets the right set.
                // Special pseudo-category "__favorites" → fetch all, filter client-side.
                const params = { limit: 100 };
                const isFavFilter = activeCategory === "__favorites";
                if (activeCategory && !isFavFilter) params.category = activeCategory;
                const [s, list, catsRes] = await Promise.all([
                    api.get(`/shops/${slug}`),
                    api.get(`/shops/${slug}/sarees`, { params }),
                    api.get(`/shops/${slug}/categories`).catch(() => ({ data: [] })),
                ]);
                setShop(s.data.shop || s.data);
                let saresList = list.data || [];
                if (isFavFilter) {
                    const favSet = new Set(getFavorites().filter((f) => f.shop_slug === slug).map((f) => f.id));
                    saresList = saresList.filter((x) => favSet.has(x.id));
                }
                setSarees(saresList);
                setCategories(catsRes.data || []);
                if (isFavFilter) {
                    setCategoryName("Your favorites");
                } else if (activeCategory) {
                    const found = (catsRes.data || []).find((c) => c.slug === activeCategory);
                    setCategoryName(found?.name || activeCategory);
                } else {
                    setCategoryName(null);
                }
                // Reset try-on cache + active selection when the catalog changes
                // so the customer sees the first item from the new filter.
                cache.current = {};
                setActiveSareeId(null);
                setTryOnImg(null);
            } catch {
                toast.error("Shop not found");
            }
        })();
    }, [slug, activeCategory]);

    // Re-sync favoriteIds whenever localStorage changes (toggled from a SareeCard heart).
    useEffect(() => {
        const sync = () => setFavoriteIds(new Set(getFavorites().filter((f) => f.shop_slug === slug).map((f) => f.id)));
        const onStorage = (e) => { if (e.key === "vastra-favs") sync(); };
        window.addEventListener("storage", onStorage);
        // also poll once on stage change so in-tab favorites land
        sync();
        return () => window.removeEventListener("storage", onStorage);
    }, [slug, stage]);

    // Start webcam when entering capture stage
    const [cameraFacing, setCameraFacing] = useState("user");  // "user" (selfie) | "environment" (rear)
    useEffect(() => {
        if (stage !== "capture") return;
        let mounted = true;
        (async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: cameraFacing, width: { ideal: 1280 }, height: { ideal: 1920 } },
                    audio: false,
                });
                if (!mounted) { stream.getTracks().forEach((t) => t.stop()); return; }
                streamRef.current = stream;
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    await videoRef.current.play();
                }
            } catch (e) {
                toast.error("Camera access denied — please allow camera permission");
            }
        })();
        return () => {
            mounted = false;
            const s = streamRef.current;
            if (s) s.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        };
    }, [stage, cameraFacing]);

    const capturePhotoNow = () => {
        const v = videoRef.current; const c = canvasRef.current;
        if (!v || !c) return;
        const w = v.videoWidth, h = v.videoHeight;
        c.width = 720; c.height = Math.round((h / w) * 720);
        const ctx = c.getContext("2d");
        ctx.save();
        ctx.scale(-1, 1);
        ctx.drawImage(v, -c.width, 0, c.width, c.height);
        ctx.restore();
        const dataUrl = c.toDataURL("image/jpeg", 0.9);
        setCustomerPhoto(dataUrl);
        const s = streamRef.current;
        if (s) s.getTracks().forEach((t) => t.stop());
        // Brief flash before transitioning
        setFlash(true);
        setTimeout(() => setFlash(false), 220);
        setStage("tryon");
        // Ask the customer to lock her face location with a single circle — this
        // is composited back on top of every Gemini try-on for pixel-perfect
        // face fidelity. Only AFTER the mask is confirmed (or skipped) do we
        // kick off the actual try-on + pre-warms, so the cache is never
        // populated with un-composited images.
        setMaskingOpen(true);
        setAnalyzing(true);
        analyzeBodyShape(dataUrl).then((res) => {
            setBodyShape(res);
            setAnalyzing(false);
            // Ask backend to rank items for this shape so we can highlight a "Recommended" row
            if (res?.shape) {
                api.post(`/shops/${slug}/mirror/suggest`, { body_shape: res.shape, limit: 12 })
                    .then(({ data }) => setRecommendedIds(data.saree_ids || []))
                    .catch(() => { /* ranking is non-critical */ });
            }
        });
    };

    /* Called once the face-mask step resolves (confirm OR skip). Kicks off the
       first try-on for the deep-linked / first eligible saree and pre-warms
       four more in the background. */
    const startTryOnFlow = (photo) => {
        const hasAI = (x) => Array.isArray(x.ai_model_images) && x.ai_model_images[0];
        const targetId = preselectedSareeId && sarees.find((x) => x.id === preselectedSareeId && hasAI(x))
            ? preselectedSareeId
            : sarees.find(hasAI)?.id;
        if (targetId) setTimeout(() => tryOn(targetId, photo), 300);
        // Parallel pre-warming — see comments above for rationale.
        const eligibleAll = sarees.filter(hasAI);
        const startIdx = targetId ? eligibleAll.findIndex((x) => x.id === targetId) : 0;
        const prewarmIds = [];
        for (let i = 1; i <= 4 && startIdx + i < eligibleAll.length; i++) {
            prewarmIds.push(eligibleAll[startIdx + i].id);
        }
        prewarmIds.forEach((id, n) => {
            setTimeout(() => tryOn(id, photo, /*silent*/ true), 1500 + n * 800);
        });
    };

    const startCountdownCapture = () => {
        if (countdown > 0) return;   // already counting
        let n = 3;
        setCountdown(n);
        countdownTimerRef.current = setInterval(() => {
            n -= 1;
            if (n <= 0) {
                clearInterval(countdownTimerRef.current);
                countdownTimerRef.current = null;
                setCountdown(0);
                capturePhotoNow();
                return;
            }
            setCountdown(n);
        }, 1000);
    };

    // Keep backward-compatible name used elsewhere
    const takePhoto = startCountdownCapture;

    // If the user re-opens the face-lock chip and adjusts the circle, the
    // previously cached try-ons were composited with the OLD mask and are now
    // stale. Wipe the cache and re-run the active saree with the new mask.
    // The initial capture flow sets maskingOpen BEFORE any tryOn fires, so
    // the first-open case is a no-op (activeSareeId still null).
    const firstMaskRef = useRef(true);
    useEffect(() => {
        if (firstMaskRef.current) { firstMaskRef.current = false; return; }
        cache.current = {};
        if (activeSareeId && customerPhoto) {
            setTryOnImg(null);
            tryOn(activeSareeId, customerPhoto);
        }
    }, [faceMask]);

    // Clean up countdown if user leaves capture stage
    useEffect(() => {
        if (stage !== "capture" && countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
            setCountdown(0);
        }
    }, [stage]);

    const tryOn = async (sareeId, customerOverride, silent = false) => {
        const photo = customerOverride || customerPhoto;
        if (!photo) return;
        // Pick the AI model photo of the saree (Gemini-generated ONCE at catalog time).
        // From here on: pure client-side canvas — zero AI calls, zero network round-trip.
        const saree = sarees.find((s) => s.id === sareeId);
        const modelUrl = saree?.ai_model_images?.[0];
        if (!modelUrl) {
            if (!silent) toast.error("This item has no AI model photo yet");
            return;
        }
        const ck = `${sareeId}::free`;
        if (silent) {
            if (cache.current[ck]) return;
            try {
                const finalImg = await swapFaceOnModel(photo, faceMask, modelUrl);
                cache.current[ck] = finalImg;
                if (activeSareeId === sareeId) setTryOnImg(finalImg);
            } catch { /* silent failure for pre-warm */ }
            return;
        }
        if (compareMode) {
            setCompareSareeId(sareeId);
            if (cache.current[ck]) { setCompareImg(cache.current[ck]); return; }
            setCompareLoading(true);
            setCompareImg(null);
            try {
                const finalImg = await swapFaceOnModel(photo, faceMask, modelUrl);
                cache.current[ck] = finalImg;
                setCompareImg(finalImg);
            } catch (e) {
                toast.error("Try-on failed");
            } finally {
                setCompareLoading(false);
            }
            return;
        }
        setActiveSareeId(sareeId);
        if (cache.current[ck]) { setTryOnImg(cache.current[ck]); return; }
        setGenerating(true);
        setTryOnImg(null);
        try {
            const finalImg = await swapFaceOnModel(photo, faceMask, modelUrl);
            cache.current[ck] = finalImg;
            setTryOnImg(finalImg);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Try-on failed");
        } finally {
            setGenerating(false);
        }
    };

    /* Switch background preset — DISABLED in FreeMirror mode (backdrop swap
       requires a Gemini regeneration). Kept as an inert stub so any lingering
       call from stale UI is a safe no-op. */
    const onBackgroundChange = (_key) => { /* no-op in FreeMirror mode */ };

    const sendStaffRequest = async () => {
        if (!staffName.trim() || !staffPhone.trim()) {
            toast.error("Please share your name and phone so the staff can find you");
            return;
        }
        setStaffSending(true);
        try {
            const saree = sarees.find((s) => s.id === activeSareeId);
            const message = saree
                ? `Please bring out "${saree.title}" for me to try in person.`
                : `Please come over and help me pick a saree.`;
            await api.post(`/staff-requests`, {
                shop_slug: slug,
                customer_name: staffName.trim(),
                customer_phone: staffPhone.trim(),
                saree_id: activeSareeId,
                message,
            });
            setStaffSent(true);
            setStaffOpen(false);
            toast.success("Staff has been notified — they'll come to you in a moment");
            setTimeout(() => setStaffSent(false), 30_000);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not notify staff — please retry");
        } finally {
            setStaffSending(false);
        }
    };

    const startOver = () => {
        cache.current = {};
        setCustomerPhoto(null);
        setTryOnImg(null);
        setActiveSareeId(null);
        setBodyShape(null);
        setCompareMode(false); setCompareSareeId(null); setCompareImg(null);
        setStaffSent(false); setStaffOpen(false); setStaffName(""); setStaffPhone("");
        setFaceMask(null); setMaskingOpen(false);
        setStage("welcome");
    };

    const retakePhoto = () => {
        cache.current = {};
        setCustomerPhoto(null);
        setTryOnImg(null);
        setActiveSareeId(null);
        setBodyShape(null);
        setCompareMode(false); setCompareSareeId(null); setCompareImg(null);
        setFaceMask(null); setMaskingOpen(false);
        setStage("capture");
    };

    const downloadImage = () => {
        if (!tryOnImg) return;
        const a = document.createElement("a");
        a.href = tryOnImg;
        a.download = `vastra-tryon-${activeSareeId?.slice(0, 8)}.jpg`;
        document.body.appendChild(a); a.click(); a.remove();
    };

    // Convert the try-on image into a cinematic Ken-Burns MP4 (free, backend ffmpeg).
    const generateReel = async () => {
        if (!tryOnImg || reelBusy) return;
        setReelBusy(true);
        setReelUrl(null);
        try {
            const { data } = await api.post(`/shops/${slug}/mirror/reel`, {
                tryon_image: tryOnImg,
                saree_id: activeSareeId,
                music: "romantic",
                duration: 10,
            });
            // data.download_url starts with "/api/…" — strip and prepend backend URL.
            const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
            const url = data.download_url?.startsWith("http")
                ? data.download_url
                : `${BACKEND}${data.download_url}`;
            setReelUrl(url);
            toast.success("Your reel is ready!");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reel generation failed");
        } finally {
            setReelBusy(false);
        }
    };

    // Any change to the try-on invalidates the previous reel.
    useEffect(() => { setReelUrl(null); }, [tryOnImg, activeSareeId]);

    const downloadReel = () => {
        if (!reelUrl) return;
        const a = document.createElement("a");
        a.href = reelUrl;
        a.download = `vastra-reel-${activeSareeId?.slice(0, 8)}.mp4`;
        document.body.appendChild(a); a.click(); a.remove();
    };

    const shareReelWhatsApp = async () => {
        if (!reelUrl) return;
        try {
            const blob = await (await fetch(reelUrl)).blob();
            const file = new File([blob], "vastra-reel.mp4", { type: "video/mp4" });
            const activeSaree = sarees.find((s) => s.id === activeSareeId);
            const text = `Look at my try-on from ${shop?.name}! ${activeSaree?.title || ""}`;
            if (navigator.canShare?.({ files: [file] })) {
                await navigator.share({ files: [file], text });
                return;
            }
        } catch { /* fall through */ }
        const text = encodeURIComponent(`Check out my try-on at ${shop?.name}!`);
        const phone = (shop?.whatsapp || shop?.phone || "").replace(/\D/g, "");
        const url = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
        window.open(url, "_blank");
    };

    const whatsappShare = async () => {
        if (!tryOnImg) return;
        try {
            const blob = await (await fetch(tryOnImg)).blob();
            const file = new File([blob], `vastra-tryon.jpg`, { type: "image/jpeg" });
            const activeSaree = sarees.find((s) => s.id === activeSareeId);
            const text = `Hi! I'm interested in this ${activeSaree?.title || "item"} from ${shop?.name}.`;
            if (navigator.canShare?.({ files: [file] })) {
                await navigator.share({ files: [file], text });
                return;
            }
        } catch { /* fall through to wa.me */ }
        const text = encodeURIComponent(`Check out my try-on at ${shop?.name}!`);
        const phone = (shop?.whatsapp || shop?.phone || "").replace(/\D/g, "");
        const url = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
        window.open(url, "_blank");
    };

    const activeSaree = useMemo(() => sarees.find((s) => s.id === activeSareeId), [sarees, activeSareeId]);

    // Eligible = any saree the customer can see + try on. The public sarees
    // endpoint already filters to sarees that have an AI model image (the
    // catalog rule: no raw uploads shown to customers), so we just need a
    // non-empty `ai_model_images` array — no length gate, because shops may
    // store images as short CDN URLs OR long base64 data URLs.
    // Favorites are surfaced FIRST so the customer naturally starts the swipe
    // queue on pieces they've already shown interest in.
    const eligible = useMemo(() => {
        const base = sarees.filter((s) => Array.isArray(s.ai_model_images) && s.ai_model_images[0]);
        if (!favoriteIds.size) return base;
        const favs = base.filter((s) => favoriteIds.has(s.id));
        const rest = base.filter((s) => !favoriteIds.has(s.id));
        return [...favs, ...rest];
    }, [sarees, favoriteIds]);

    // Items ranked by the backend's body-shape recommender; defined even when no
    // body shape was detected (falls back to empty array).
    const recommended = useMemo(() => {
        if (!recommendedIds.length) return [];
        const byId = new Map(eligible.map((s) => [s.id, s]));
        return recommendedIds.map((id) => byId.get(id)).filter(Boolean).slice(0, 6);
    }, [recommendedIds, eligible]);

    /* Prev / next saree navigation — drives both the swipe gesture on the
       try-on image and the on-image arrow buttons. Wraps around so the customer
       can scroll a 30-saree catalog continuously. */
    const currentIdx = useMemo(
        () => Math.max(0, eligible.findIndex((s) => s.id === activeSareeId)),
        [eligible, activeSareeId],
    );
    const goSaree = (delta) => {
        if (!eligible.length || compareMode) return;
        const n = eligible.length;
        const next = ((currentIdx + delta) % n + n) % n;   // wrap-around
        const target = eligible[next];
        if (target) tryOn(target.id);
    };
    const goNextSaree = () => goSaree(+1);
    const goPrevSaree = () => goSaree(-1);

    /* Swipe handler for the main try-on image. Up = next, down = prev.
       NOTE: We deliberately do NOT bind a wheel handler here — on laptops,
       casual page scrolling would otherwise burn through Gemini quota by
       firing tryOn() per wheel tick. The Reel Mode overlay keeps wheel
       support because it's a dedicated fullscreen viewer. */
    const swipeStartYRef = useRef(null);
    const onImageTouchStart = (e) => { swipeStartYRef.current = e.touches[0]?.clientY ?? null; };
    const onImageTouchEnd = (e) => {
        const start = swipeStartYRef.current;
        if (start == null) return;
        const dy = start - (e.changedTouches[0]?.clientY ?? start);
        swipeStartYRef.current = null;
        if (Math.abs(dy) < 40) return;  // ignore taps
        if (dy > 0) goNextSaree(); else goPrevSaree();
    };

    /* ---------- WELCOME ---------- */
    if (stage === "welcome") {
        return (
            <div className="relative min-h-screen bg-brand-ink text-brand-paper overflow-hidden flex flex-col items-center justify-center px-6" data-testid="mirror-welcome">
                {/* subtle bronze radial glow behind everything */}
                <div className="absolute inset-0 pointer-events-none" style={{
                    background: "radial-gradient(ellipse at center, rgba(163,122,82,0.18) 0%, transparent 55%)"
                }} />
                {/* corner overline */}
                <div className="absolute top-8 left-8 editorial-eyebrow text-brand-bronzelight">
                    <span className="inline-block w-6 h-px bg-brand-bronze align-middle mr-2.5" />
                    Vastra · Mirror
                </div>
                <div className="absolute top-8 right-8 hidden sm:flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider-2 text-brand-bronzelight">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-soft-pulse" />
                    {eligible.length} pieces ready
                </div>

                {/* central animated ring */}
                <div className="relative mb-12">
                    <div className="absolute inset-0 rounded-full border border-brand-bronze/40 animate-soft-pulse" />
                    <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full border border-brand-bronze/60 grid place-items-center backdrop-blur-2xl bg-white/[0.03]">
                        <Sparkles className="w-7 h-7 sm:w-8 sm:h-8 text-brand-bronzelight" strokeWidth={1} />
                    </div>
                </div>

                {/* headline */}
                <div className="text-center max-w-2xl animate-fade-up">
                    <h1 className="font-display text-5xl sm:text-7xl lg:text-[88px] tracking-editorial leading-[0.95] text-balance">
                        <em className="not-italic text-brand-bronzelight">{shop?.name || "Welcome"}</em>
                    </h1>
                    <p className="text-white/65 mt-7 max-w-md mx-auto text-base sm:text-lg leading-relaxed text-pretty">
                        Step into the mirror. Drape yourself in
                        <span className="font-mono text-brand-bronzelight"> {eligible.length} </span>
                        {eligible.length === 1 ? "piece" : "pieces"} in seconds.
                    </p>
                </div>

                <button
                    className="mt-14 inline-flex items-center justify-center bg-brand-bronze text-white hover:bg-brand-bronzehover transition-all duration-500 px-10 py-4 text-sm font-medium tracking-wider-2 uppercase font-mono rounded-sm shadow-bronze-glow hover:-translate-y-0.5"
                    onClick={() => setStage("capture")}
                    data-testid="mirror-start-btn"
                >
                    <Camera className="w-4 h-4 mr-3" strokeWidth={1.5} /> Step inside
                </button>

                <p className="text-[11px] text-white/40 mt-10 font-mono uppercase tracking-wider-2 text-center max-w-sm">
                    No login · No data stored · Your photo stays on this device
                </p>
            </div>
        );
    }

    /* ---------- CAPTURE ---------- */
    if (stage === "capture") {
        const counting = countdown > 0;
        return (
            <div className="min-h-screen bg-brand-ink flex flex-col items-center justify-center p-4" data-testid="mirror-capture">
                <button
                    onClick={() => setStage("welcome")}
                    className="absolute top-6 left-6 inline-flex items-center gap-2 text-white/60 hover:text-white text-xs font-mono uppercase tracking-wider-2 transition"
                    data-testid="mirror-back-btn"
                >
                    <ChevronLeft className="w-3.5 h-3.5" /> Back
                </button>
                <div className="font-display text-2xl sm:text-3xl text-white mb-6 tracking-editorial text-center">
                    {counting ? <em className="not-italic text-brand-bronzelight">Hold still…</em> : "Stand straight, look at the mirror"}
                </div>
                <div className="relative max-w-md w-full aspect-[3/4] rounded-sm overflow-hidden border border-brand-bronze/40">
                    <video ref={videoRef} className="w-full h-full object-cover" style={{ transform: cameraFacing === "user" ? "scaleX(-1)" : "none" }} autoPlay playsInline muted />
                    <div className="absolute inset-x-8 top-12 bottom-32 border border-dashed border-white/40 rounded-sm pointer-events-none" />
                    {/* Flip camera button */}
                    <button
                        type="button"
                        onClick={() => setCameraFacing((f) => f === "user" ? "environment" : "user")}
                        className="absolute top-3 right-3 w-10 h-10 grid place-items-center bg-black/55 hover:bg-black/75 text-white backdrop-blur-sm rounded-full transition"
                        title="Flip camera"
                        aria-label="Flip camera"
                        data-testid="mirror-flip-camera"
                    >
                        <SwitchCamera className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                    {counting ? (
                        <div className="absolute inset-0 grid place-items-center pointer-events-none" data-testid="mirror-countdown">
                            <div
                                key={countdown}
                                className="font-display text-white text-[180px] leading-none drop-shadow-[0_4px_30px_rgba(0,0,0,0.7)]"
                                style={{ animation: 'fade-up 0.6s cubic-bezier(.22,1,.36,1) forwards' }}
                            >
                                {countdown}
                            </div>
                        </div>
                    ) : null}
                    {flash ? <div className="absolute inset-0 bg-white animate-pulse" /> : null}
                </div>
                <canvas ref={canvasRef} className="hidden" />
                <button
                    className="mt-8 inline-flex items-center justify-center bg-brand-bronze text-white hover:bg-brand-bronzehover transition-all duration-300 px-10 py-4 text-sm font-medium tracking-wider-2 uppercase font-mono rounded-sm shadow-bronze-glow disabled:opacity-60"
                    onClick={takePhoto}
                    disabled={counting}
                    data-testid="mirror-capture-btn"
                >
                    <Camera className="w-4 h-4 mr-3" strokeWidth={1.5} />
                    {counting ? `Capturing in ${countdown}…` : "Take photo"}
                </button>
                {!counting ? (
                    <p className="text-[11px] text-white/40 mt-4 font-mono uppercase tracking-wider-2">We&apos;ll wait 3 seconds for you to pose.</p>
                ) : null}
            </div>
        );
    }

    /* ---------- TRY-ON ---------- */
    return (
        <div className="min-h-screen bg-brand-paper" data-testid="mirror-tryon">
            <header className="flex items-center justify-between px-4 sm:px-8 py-4 border-b border-brand-border">
                <div className="min-w-0">
                    <div className="ayraa-overline truncate">{shop?.name}</div>
                    <div className="font-serif text-lg sm:text-xl">
                        Try on
                        {categoryName ? (
                            <span className="ml-2 text-sm align-middle bg-brand-primary text-white px-2 py-0.5 rounded-full" data-testid="mirror-category-banner">
                                {categoryName}
                            </span>
                        ) : null}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={retakePhoto} className="ayraa-btn-ghost text-xs sm:text-sm" data-testid="mirror-retake-btn">
                        <RefreshCw className="w-3.5 h-3.5 mr-1" /> Retake photo
                    </button>
                    <button onClick={startOver} className="ayraa-btn-ghost text-xs sm:text-sm" data-testid="mirror-startover-btn">
                        <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Start over
                    </button>
                </div>
            </header>

            <main className="flex flex-col lg:flex-row gap-4 p-4 sm:p-6 max-w-7xl mx-auto">
                {/* Body shape hint */}
                {bodyShape ? (
                    <div className="lg:hidden ayraa-card p-3 flex items-start gap-2 text-xs" data-testid="mirror-bodyshape-hint">
                        <Sparkles className="w-4 h-4 text-brand-primary mt-0.5 shrink-0" strokeWidth={1.5} />
                        <div>
                            <span className="font-medium text-brand-primary">{bodyShape.shape}</span>
                            <span className="text-brand-muted ml-1.5">{bodyShape.hint}</span>
                        </div>
                    </div>
                ) : analyzing ? (
                    <div className="lg:hidden text-xs text-brand-soft px-1">Analyzing your fit…</div>
                ) : null}

                {/* Try-on display */}
                <div className="flex-1 min-h-[520px]">
                    {/* Catalog (category) chip row — filters the picker, recommended,
                        Reel mode and prev/next swipe to a single category. */}
                    {categories.length > 0 ? (
                        <div className="max-w-md mx-auto mb-3" data-testid="mirror-category-chips">
                            <div className="ayraa-overline mb-1.5 px-1 flex items-center gap-1">
                                <Layers className="w-3 h-3 text-brand-primary" strokeWidth={1.5} />
                                Catalog
                            </div>
                            <div className="flex gap-2 overflow-x-auto scroll-x px-1 pb-1 -mx-1">
                                <button
                                    type="button"
                                    onClick={() => setActiveCategory("")}
                                    className={`shrink-0 px-3 py-1.5 rounded-full text-xs whitespace-nowrap border-2 transition ${
                                        !activeCategory
                                            ? "border-brand-primary bg-brand-primary text-white"
                                            : "border-brand-gold/40 bg-brand-paper text-brand-dark hover:border-brand-primary"
                                    }`}
                                    data-testid="mirror-cat-all"
                                >
                                    All
                                </button>
                                {favoriteIds.size > 0 ? (
                                    <button
                                        type="button"
                                        onClick={() => setActiveCategory("__favorites")}
                                        className={`shrink-0 px-3 py-1.5 rounded-full text-xs whitespace-nowrap border-2 transition flex items-center gap-1.5 ${
                                            activeCategory === "__favorites"
                                                ? "border-brand-primary bg-brand-primary text-white"
                                                : "border-brand-gold/40 bg-brand-paper text-brand-dark hover:border-brand-primary"
                                        }`}
                                        data-testid="mirror-cat-favorites"
                                        title="Show only sarees you've favorited"
                                    >
                                        <Heart className={`w-3 h-3 ${activeCategory === "__favorites" ? "fill-current" : "fill-brand-primary text-brand-primary"}`} strokeWidth={1.5} />
                                        Favorites <span className="font-mono opacity-70">{favoriteIds.size}</span>
                                    </button>
                                ) : null}
                                {categories.map((c) => (
                                    <button
                                        key={c.slug}
                                        type="button"
                                        onClick={() => setActiveCategory(c.slug)}
                                        className={`shrink-0 px-3 py-1.5 rounded-full text-xs whitespace-nowrap border-2 transition ${
                                            activeCategory === c.slug
                                                ? "border-brand-primary bg-brand-primary text-white"
                                                : "border-brand-gold/40 bg-brand-paper text-brand-dark hover:border-brand-primary"
                                        }`}
                                        data-testid={`mirror-cat-${c.slug}`}
                                    >
                                        {c.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    {/* Compare-mode toggle row */}
                    <div className="flex items-center justify-between gap-2 mb-2 px-1 max-w-md mx-auto">
                        <button
                            type="button"
                            className={`text-xs px-3 py-1.5 rounded-full border transition ${compareMode ? "bg-brand-primary text-white border-brand-primary" : "bg-brand-paper border-brand-border text-brand-muted"}`}
                            onClick={() => {
                                if (compareMode) { setCompareSareeId(null); setCompareImg(null); }
                                setCompareMode((v) => !v);
                            }}
                            data-testid="mirror-compare-toggle"
                        >
                            <GitCompareArrows className="w-3.5 h-3.5 inline mr-1" /> {compareMode ? "Exit compare" : "Compare 2 sarees"}
                        </button>
                        {/* Reel-mode entry — Instagram-style vertical scroll through every saree */}
                        {!compareMode && eligible.length > 1 ? (
                            <button
                                type="button"
                                className="text-xs px-3 py-1.5 rounded-full border bg-brand-dark text-white border-brand-dark"
                                onClick={() => setReelMode(true)}
                                data-testid="mirror-reel-mode-btn"
                            >
                                <Film className="w-3.5 h-3.5 inline mr-1" /> Reel mode
                            </button>
                        ) : null}
                        {compareMode ? (
                            <span className="text-[11px] text-brand-soft">Tap a second saree →</span>
                        ) : null}
                    </div>

                    {compareMode ? (
                        <div className="grid grid-cols-2 gap-2 max-w-2xl mx-auto">
                            <ComparePanel label={activeSaree?.title || "Pick saree 1"} img={tryOnImg} loading={generating} />
                            <ComparePanel label={(sarees.find((s) => s.id === compareSareeId)?.title) || "Pick saree 2"} img={compareImg} loading={compareLoading} alt />
                        </div>
                    ) : (
                        <div
                            className="relative w-full aspect-[3/4] max-w-md mx-auto rounded-xl overflow-hidden bg-black/5 border border-brand-border touch-none select-none"
                            onTouchStart={onImageTouchStart}
                            onTouchEnd={onImageTouchEnd}
                            data-testid="mirror-tryon-stage"
                        >
                            {tryOnImg ? (
                                <img src={tryOnImg} alt="try-on" className="w-full h-full object-cover pointer-events-none" data-testid="mirror-tryon-img" />
                            ) : (
                                <img src={customerPhoto} alt="customer" className="w-full h-full object-cover opacity-60 pointer-events-none" data-testid="mirror-original-img" />
                            )}
                            {/* Face-lock chip — tap to re-adjust the face circle. */}
                            {customerPhoto ? (
                                <button
                                    type="button"
                                    onClick={() => setMaskingOpen(true)}
                                    className={`absolute top-2 right-2 z-30 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-wider-2 backdrop-blur-md shadow-lg transition min-h-[32px] ${
                                        faceMask
                                            ? "bg-brand-bronze/90 text-white hover:bg-brand-bronze"
                                            : "bg-black/60 text-white/85 hover:bg-black/75"
                                    }`}
                                    data-testid="face-lock-chip"
                                    title={faceMask ? "Face locked — tap to re-adjust" : "Face not locked — tap to lock"}
                                >
                                    {faceMask ? "◉ Face locked" : "◯ Lock face"}
                                </button>
                            ) : null}
                            {generating ? (
                                <div className="absolute inset-0 grid place-items-center bg-black/50 text-white" data-testid="mirror-loading">
                                    <div className="text-center">
                                        <Loader2 className="w-10 h-10 mx-auto animate-spin" strokeWidth={1.2} />
                                        <div className="font-serif text-lg mt-3">Draping you in this one…</div>
                                        <div className="text-xs text-white/70 mt-1">~8 seconds</div>
                                    </div>
                                </div>
                            ) : null}

                            {/* Prev / next saree arrows — overlay on the image. */}
                            {eligible.length > 1 ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={goPrevSaree}
                                        className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 grid place-items-center rounded-full bg-black/55 hover:bg-black/75 text-white backdrop-blur-sm transition shadow-lg"
                                        aria-label="Previous saree"
                                        data-testid="mirror-prev-saree"
                                    >
                                        <ChevronUp className="w-5 h-5" strokeWidth={2} />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={goNextSaree}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 grid place-items-center rounded-full bg-black/55 hover:bg-black/75 text-white backdrop-blur-sm transition shadow-lg"
                                        aria-label="Next saree"
                                        data-testid="mirror-next-saree"
                                    >
                                        <ChevronDown className="w-5 h-5" strokeWidth={2} />
                                    </button>
                                    {/* Position indicator + swipe hint */}
                                    <div className="absolute top-2 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-full bg-black/55 text-white text-[10px] tracking-wide backdrop-blur-sm" data-testid="mirror-saree-pos">
                                        {currentIdx + 1} / {eligible.length} · swipe ↑↓
                                    </div>
                                </>
                            ) : null}
                        </div>
                    )}

                    {tryOnImg ? (
                        <div className="max-w-md mx-auto mt-4 flex flex-wrap gap-2 justify-center">
                            <button
                                className={`ayraa-btn-primary ${staffSent ? "opacity-60" : ""}`}
                                onClick={() => setStaffOpen(true)}
                                disabled={staffSent}
                                data-testid="mirror-staff-btn"
                            >
                                <Bell className="w-4 h-4 mr-1.5" /> {staffSent ? "Staff notified" : "Call staff"}
                            </button>
                            <button className="ayraa-btn-outline" onClick={whatsappShare} data-testid="mirror-whatsapp-btn">
                                <Share2 className="w-4 h-4 mr-1.5" /> Send to shop
                            </button>
                            <button className="ayraa-btn-outline" onClick={downloadImage} data-testid="mirror-download-btn">
                                <Download className="w-4 h-4 mr-1.5" /> Save photo
                            </button>
                            <button
                                className="ayraa-btn-outline"
                                onClick={generateReel}
                                disabled={reelBusy}
                                data-testid="mirror-reel-btn"
                            >
                                {reelBusy ? (
                                    <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Making reel…</>
                                ) : reelUrl ? (
                                    <><Video className="w-4 h-4 mr-1.5" /> Regenerate reel</>
                                ) : (
                                    <><Video className="w-4 h-4 mr-1.5" /> Watch as video</>
                                )}
                            </button>
                            <a className="ayraa-btn-outline" href={`/shop/${slug}/saree/${activeSareeId}`} target="_blank" rel="noreferrer">
                                <Heart className="w-4 h-4 mr-1.5" /> See details
                            </a>
                        </div>
                    ) : null}

                    {reelUrl ? (
                        <div className="max-w-sm mx-auto mt-6 space-y-3" data-testid="mirror-reel-player">
                            <div className="editorial-eyebrow text-center">
                                <span className="inline-block w-6 h-px bg-brand-bronze align-middle mr-2.5" />
                                Cinematic reel · free
                            </div>
                            <video
                                src={reelUrl}
                                controls
                                autoPlay
                                loop
                                playsInline
                                className="w-full aspect-[9/16] rounded-sm border border-brand-border bg-black"
                                data-testid="mirror-reel-video"
                            />
                            <div className="flex gap-2 justify-center">
                                <button className="ayraa-btn-outline" onClick={shareReelWhatsApp} data-testid="mirror-reel-share-btn">
                                    <Share2 className="w-4 h-4 mr-1.5" /> Share reel
                                </button>
                                <button className="ayraa-btn-outline" onClick={downloadReel} data-testid="mirror-reel-download-btn">
                                    <Download className="w-4 h-4 mr-1.5" /> Save reel
                                </button>
                            </div>
                        </div>
                    ) : null}
                </div>

                {/* Saree carousel */}
                <aside className="lg:w-[360px] lg:max-w-[360px]">
                    {bodyShape ? (
                        <div className="hidden lg:flex ayraa-card p-3 items-start gap-2 mb-3 text-xs" data-testid="mirror-bodyshape-hint-desktop">
                            <Sparkles className="w-4 h-4 text-brand-primary mt-0.5 shrink-0" strokeWidth={1.5} />
                            <div>
                                <div className="font-medium text-brand-primary">{bodyShape.shape}</div>
                                <div className="text-brand-muted mt-0.5">{bodyShape.hint}</div>
                            </div>
                        </div>
                    ) : null}

                    {/* Background scene picker — DISABLED in FreeMirror mode.
                        Backdrop swap requires a Gemini regeneration; in the free
                        client-side flow we display the item's baked-in scene.
                        Kept in code for a future AI-mode toggle. */}
                    {false ? (
                    <div className="mb-3" data-testid="mirror-bg-picker">
                        <div className="ayraa-overline mb-2 px-1 flex items-center gap-1">
                            <Frame className="w-3 h-3 text-brand-primary" strokeWidth={1.5} />
                            Backdrop
                        </div>
                        <div className="flex gap-2 overflow-x-auto scroll-x px-1 pb-2 -mx-1">
                            {BACKGROUND_PRESETS.map((b) => (
                                <button
                                    key={b.key}
                                    onClick={() => onBackgroundChange(b.key)}
                                    disabled={generating}
                                    className={`shrink-0 px-3 py-2 rounded-full text-xs whitespace-nowrap border-2 transition ${
                                        background === b.key
                                            ? "border-brand-primary bg-brand-primary text-white"
                                            : "border-brand-gold/40 bg-brand-paper text-brand-dark hover:border-brand-primary"
                                    } ${generating ? "opacity-50 cursor-not-allowed" : ""}`}
                                    data-testid={`mirror-bg-${b.key}`}
                                    title={b.desc}
                                >
                                    <span className="mr-1">{b.emoji}</span>{b.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    ) : null}

                    {/* Auto-suggested items for this customer's body shape */}
                    {recommended.length ? (
                        <div className="mb-3" data-testid="mirror-recommended">
                            <div className="ayraa-overline mb-2 px-1 flex items-center gap-1">
                                <Sparkles className="w-3 h-3 text-brand-primary" strokeWidth={1.5} />
                                Recommended for you
                            </div>
                            <div className="flex gap-2 overflow-x-auto scroll-x px-1 pb-2 -mx-1">
                                {recommended.map((s) => {
                                    const isMain = s.id === activeSareeId;
                                    const isCmp = s.id === compareSareeId;
                                    return (
                                        <button
                                            key={s.id}
                                            onClick={() => tryOn(s.id)}
                                            className={`relative shrink-0 w-20 aspect-[3/4] rounded-lg overflow-hidden border-2 transition ${
                                                isMain ? "border-brand-primary ring-2 ring-brand-primary/30"
                                                : isCmp ? "border-brand-gold ring-2 ring-brand-gold/30"
                                                : "border-brand-gold/40 hover:border-brand-primary"
                                            }`}
                                            data-testid={`mirror-recommended-${s.id}`}
                                        >
                                            <img src={s.ai_model_images?.[0] || s.images?.[0]} alt={s.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                                            <div className="absolute top-1 right-1 bg-brand-gold text-brand-dark text-[8px] font-medium px-1 rounded uppercase tracking-wider">Pick</div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}

                    <div className="ayraa-overline mb-2 px-1">Pick a saree</div>
                    {eligible.length === 0 ? (
                        <div className="text-sm text-brand-muted p-4 ayraa-card">No items ready for try-on yet. Ask the shop to generate AI photos first.</div>
                    ) : (
                        <div className="grid grid-cols-3 lg:grid-cols-2 gap-2 max-h-[70vh] overflow-y-auto px-1 pb-3">
                            {eligible.map((s) => {
                                const isMain = s.id === activeSareeId;
                                const isCmp = s.id === compareSareeId;
                                return (
                                    <button
                                        key={s.id}
                                        onClick={() => tryOn(s.id)}
                                        className={`relative aspect-[3/4] rounded-lg overflow-hidden border-2 transition ${
                                            isMain ? "border-brand-primary ring-2 ring-brand-primary/30"
                                            : isCmp ? "border-brand-gold ring-2 ring-brand-gold/30"
                                            : "border-transparent hover:border-brand-border"
                                        }`}
                                        data-testid={`mirror-saree-${s.id}`}
                                    >
                                        <img src={s.ai_model_images?.[0] || s.images?.[0]} alt={s.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                                        <div className="absolute bottom-0 inset-x-0 px-1.5 py-1 text-[10px] text-white bg-gradient-to-t from-black/80 to-transparent line-clamp-1">
                                            {s.title}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </aside>
            </main>

            {/* Staff request modal */}
            {staffOpen ? (
                <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setStaffOpen(false)} data-testid="mirror-staff-modal">
                    <div className="bg-brand-paper rounded-xl max-w-sm w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setStaffOpen(false)} className="absolute top-3 right-3 text-brand-muted" aria-label="Close">
                            <X className="w-5 h-5" />
                        </button>
                        <div className="font-serif text-xl">Call staff</div>
                        <p className="text-sm text-brand-muted mt-1">We&apos;ll let them know you&apos;re at the mirror and which saree you&apos;d like to physically try.</p>
                        <div className="mt-4 space-y-3">
                            <div>
                                <label className="ayraa-overline">Your name</label>
                                <input className="ayraa-input mt-1.5" value={staffName} onChange={(e) => setStaffName(e.target.value)} data-testid="mirror-staff-name" />
                            </div>
                            <div>
                                <label className="ayraa-overline">Phone</label>
                                <input className="ayraa-input mt-1.5" type="tel" value={staffPhone} onChange={(e) => setStaffPhone(e.target.value)} data-testid="mirror-staff-phone" />
                            </div>
                        </div>
                        <button
                            className="ayraa-btn-primary w-full mt-4"
                            onClick={sendStaffRequest}
                            disabled={staffSending}
                            data-testid="mirror-staff-send"
                        >
                            {staffSending ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending…</> : <><Bell className="w-4 h-4 mr-1.5" /> Notify staff</>}
                        </button>
                    </div>
                </div>
            ) : null}

            {/* Reel mode — Instagram-style vertical scroll through every saree.
                Renders as a fullscreen overlay so the parent UI stays mounted
                (preserves camera, cache, body shape) when the customer exits. */}
            {reelMode && customerPhoto && eligible.length > 0 ? (
                <MirrorReelMode
                    sarees={eligible}
                    initialIndex={Math.max(0, eligible.findIndex((s) => s.id === activeSareeId))}
                    tryOn={tryOn}
                    cache={cache}
                    background={background}
                    generating={generating}
                    tryOnImg={tryOnImg}
                    activeSareeId={activeSareeId}
                    onExit={() => setReelMode(false)}
                />
            ) : null}

            {/* Face-mask step — appears once, right after photo capture.
                Confirming or skipping starts the try-on flow. */}
            {maskingOpen && customerPhoto ? (
                <FaceMaskDrawer
                    photo={customerPhoto}
                    onDone={(m) => {
                        setFaceMask(m);
                        setMaskingOpen(false);
                        startTryOnFlow(customerPhoto);
                    }}
                    onSkip={() => {
                        setFaceMask(null);
                        setMaskingOpen(false);
                        startTryOnFlow(customerPhoto);
                    }}
                />
            ) : null}
        </div>
    );
}

function ComparePanel({ label, img, loading, alt }) {
    return (
        <div className={`relative aspect-[3/4] rounded-lg overflow-hidden border ${alt ? "border-brand-gold/40" : "border-brand-primary/40"} bg-black/5`}>
            {img ? (
                <img src={img} alt={label} className="w-full h-full object-cover" />
            ) : (
                <div className="w-full h-full grid place-items-center text-xs text-brand-muted px-3 text-center">{label}</div>
            )}
            {loading ? (
                <div className="absolute inset-0 grid place-items-center bg-black/50 text-white">
                    <Loader2 className="w-6 h-6 animate-spin" strokeWidth={1.2} />
                </div>
            ) : null}
            <div className="absolute bottom-0 inset-x-0 px-2 py-1 text-[10px] text-white bg-gradient-to-t from-black/80 to-transparent line-clamp-1 truncate">{label}</div>
        </div>
    );
}
